// External dependencies
import { Button, ColorIndicator, ColorPicker, Popover } from '@wordpress/components';
import { useState } from '@wordpress/element';

/**
 * Carousel Color Control Component
 * 
 * A dedicated color control for carousel settings that uses local state
 * instead of attribute-based toggle state. This prevents React hooks
 * violations when used inside conditional renders.
 * 
 * @param {Object} props Component props.
 * @param {Object} props.attributes Current attributes.
 * @param {Function} props.updateAttributes Function to update attributes.
 * @param {string} props.attribute The attribute key for this color.
 * @param {string} props.labelText Label text for the button.
 * @param {string} props.resetText Reset button text.
 * @param {string} props.defaultValue Default color value.
 */
const CarouselColorControl = ( {
	attributes,
	updateAttributes,
	attribute,
	labelText,
	resetText,
	defaultValue = '',
} ) => {
	const [ isOpen, setIsOpen ] = useState( false );

	// Get current value from attributes
	const currentValue = attributes[ attribute ] || '';
	
	// Extract hex color - handle both string and object formats
	let displayColor = '';
	if ( currentValue ) {
		if ( typeof currentValue === 'object' && currentValue.hex ) {
			displayColor = currentValue.hex;
		} else if ( typeof currentValue === 'string' ) {
			displayColor = currentValue;
		}
	}
	if ( ! displayColor ) {
		displayColor = defaultValue;
	}

	// Handle color change - always store as hex string for carousel
	const handleColorChange = ( value ) => {
		let hexColor = '';
		if ( typeof value === 'string' ) {
			hexColor = value;
		} else if ( value && value.hex ) {
			hexColor = value.hex;
		}
		if ( hexColor ) {
			updateAttributes( { [ attribute ]: hexColor } );
		}
	};

	// Handle reset
	const handleReset = () => {
		updateAttributes( { [ attribute ]: '' } );
	};

	return (
		<>
			<Button
				variant="tertiary"
				label={ labelText }
				onClick={ () => setIsOpen( true ) }
				style={ {
					flexDirection: 'column-reverse',
					height: '60px',
					gap: '8px',
					alignItems: 'flex-start',
				} }
			>
				<ColorIndicator colorValue={ displayColor } />
				<span style={ { color: '#1B2559' } }>{ labelText }</span>
			</Button>
			{ isOpen && (
				<Popover
					animate={ true }
					flip={ true }
					expandOnMobile={ true }
					onClose={ () => setIsOpen( false ) }
				>
					<ColorPicker
						color={ displayColor }
						enableAlpha={ true }
						onChangeComplete={ handleColorChange }
					/>
					<Button
						variant="tertiary"
						onClick={ handleReset }
						style={ {
							padding: '5px 10px',
							marginLeft: '18px',
							marginBottom: '18px',
						} }
					>
						{ resetText }
					</Button>
				</Popover>
			) }
		</>
	);
};

export default CarouselColorControl;
